import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useSmartComment } from '../hooks/useSmartComment';
import { ChevronDown, ChevronUp, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const typeIcons = {
    golf: '⛳', hiking: '🥾', swimming: '🏊', strength: '💪', cardio: '🏃',
    yoga: '🧘', hiit: '⚡', sleep: '😴', nutrition: '🍎', health: '❤️',
    cycling: '🚴', walking: '🚶'
};

export function NanoukJournal({ language }) {
    const { history, loadHistory } = useSmartComment();
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => { loadHistory(7); }, []);

    if (history.length === 0) return null;

    const refreshNote = async (id) => {
        setBusyId(id);
        try {
            await axios.post(`${API}/api/coach/smart-comments/${id}/refresh?language=${language}`);
            await loadHistory(7);
            toast.success(language === 'de' ? 'Notiz neu bewertet' : 'Note re-assessed');
        } catch {
            toast.error(language === 'de' ? 'Neu-Bewertung fehlgeschlagen' : 'Re-assessment failed');
        } finally {
            setBusyId(null);
        }
    };

    const shown = !open ? history.slice(0, 1) : (expanded ? history : history.slice(0, 3));

    return (
        <Card className="border" data-testid="nanouk-journal">
            <CardContent className="p-4">
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className="w-full flex items-center justify-between mb-3 text-left"
                    data-testid="nanouk-journal-toggle"
                    aria-expanded={open}
                >
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                        <span className="text-lg">🐺</span>
                        {language === 'de' ? 'Nanouks Notizen' : "Nanouk's Notes"}
                    </h3>
                    <span className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        {history.length} {language === 'de' ? 'Einträge' : 'entries'}
                        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>
                </button>
                <div className="space-y-2">
                    {shown.map((c, i) => (
                        <div key={c.id || i} className="flex gap-2 p-2 rounded-lg bg-muted/50 text-sm">
                            <span className="shrink-0 mt-0.5">{typeIcons[c.activity_type] || '🐺'}</span>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs leading-relaxed">{c.comment}</p>
                                <div className="flex items-center justify-between mt-1">
                                    <p className="text-[9px] text-muted-foreground">
                                        {c.created_at ? new Date(c.created_at).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                                        {c.refreshed_at && (
                                            <span className="ml-1.5 text-emerald-600">
                                                · {language === 'de' ? 'neu bewertet' : 're-assessed'}
                                            </span>
                                        )}
                                    </p>
                                    {c.id && (
                                        <button
                                            onClick={() => refreshNote(c.id)}
                                            disabled={busyId !== null}
                                            className="p-1 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                                            title={language === 'de' ? 'Mit aktuellen Daten neu bewerten' : 'Re-assess with current data'}
                                            data-testid={`journal-refresh-${c.id}`}
                                        >
                                            {busyId === c.id
                                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                                : <RefreshCw className="w-3 h-3" />}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {!open && history.length > 1 && (
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="w-full mt-2 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="nanouk-journal-open"
                    >
                        <ChevronDown className="w-3 h-3 inline mr-1" />
                        {language === 'de' ? `${history.length - 1} weitere anzeigen` : `Show ${history.length - 1} more`}
                    </button>
                )}
                {open && history.length > 3 && (
                    <Button variant="ghost" size="sm" className="w-full mt-2 text-xs" onClick={() => setExpanded(!expanded)}>
                        {expanded ? <><ChevronUp className="w-3 h-3 mr-1" />{language === 'de' ? 'Weniger' : 'Less'}</> : <><ChevronDown className="w-3 h-3 mr-1" />{language === 'de' ? `Alle ${history.length} anzeigen` : `Show all ${history.length}`}</>}
                    </Button>
                )}
            </CardContent>
        </Card>
    );
}
